import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { getMonth, listMonths } from './api';
import type { Month, MonthStatus } from './types';

export type MonthsState =
  { status: 'loading' } | { status: 'error' } | { status: 'ready'; items: MonthStatus[] };

export type MonthState =
  { status: 'loading' } | { status: 'error' } | { status: 'ready'; month: Month };

/** The months list, and which month the page shows: `?month=` or else the newest. */
export function useMonths() {
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState<MonthsState>({ status: 'loading' });
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let active = true;
    void listMonths().then((answer) => {
      if (!active) return;
      setState(answer.ok ? { status: 'ready', items: answer.value.items } : { status: 'error' });
    });
    return () => {
      active = false;
    };
  }, [reloads]);

  const chosen = params.get('month');
  const newest = state.status === 'ready' ? state.items[0]?.month : undefined;
  const select = useCallback(
    (month: string) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('month', month);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  const reload = useCallback(() => setReloads((n) => n + 1), []);
  return { state, month: chosen ?? newest, select, reload } as const;
}

/** One month with what is in, what blocks and the warnings; `adopt` takes what an action answered. */
export function useMonth(month: string | undefined) {
  const [state, setState] = useState<MonthState>({ status: 'loading' });
  const [reloads, setReloads] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    if (month === undefined) return;
    const mine = ++generation.current;
    // A re-read of the month on screen keeps it there meanwhile; another month starts empty.
    setState((prev) =>
      prev.status === 'ready' && prev.month.month === month ? prev : { status: 'loading' },
    );
    void getMonth(month).then((answer) => {
      if (mine !== generation.current) return;
      setState(answer.ok ? { status: 'ready', month: answer.value } : { status: 'error' });
    });
  }, [month, reloads]);

  const adopt = useCallback((next: Month) => setState({ status: 'ready', month: next }), []);
  const reload = useCallback(() => setReloads((n) => n + 1), []);
  return { state, adopt, reload } as const;
}
